/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, OutlinedInput, styled, TextField, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { apiRequest } from "../../../common/common";
import { showNotification } from "../../../common/Notification/ToastifyNotification";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const CustomFilterPanelWrapper = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: 10,
});

// The content area where filter controls will go
const FilterContent = styled(Box)({
    flexGrow: 1,         // Take up available space
    overflowY: 'auto',   // Enable scrolling if content overflows
    paddingBottom: 30,   // Ensure space for the fixed footer
});

// Footer area with fixed button
const FilterFooter = styled(Box)({
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    gap: 5
});

// Fixed button in the footer
const FixedButton = styled(Button)({
    width: '100%',
});

export default function FilterPanel({
    gridApi, applyFilter
}) {
    const [expanded, setExpanded] = useState(false);
    const [manufacturerData, setManufacturerData] = useState([]);
    const [manufacturerPage, setManufacturerPage] = useState(1);
    const [isManufacturerAvailable, setIsManufacturerAvailable] = useState(true);
    const [manufacturerLoading, setManufacturerLoading] = useState(false);
    const [categoryData, setCategoryData] = useState([]);
    const [categoryPage, setCategoryPage] = useState(1);
    const [isCategoryAvailable, setIsCategoryAvailable] = useState(true);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [subCategoryData, setSubCategoryData] = useState([]);
    const [subCategoryPage, setSubCategoryPage] = useState(1);
    const [isSubCategoryAvailable, setIsSubCategoryAvailable] = useState(true);
    const [subCategoryLoading, setSubCategoryLoading] = useState(false);
    const [filters, setFilters] = useState(localStorage.getItem('filters') ? JSON.parse(localStorage.getItem('filters')) : {});
    const [inputValue, setInputValue] = useState('');
    const [categoryInput, setCategoryInput] = useState('');

    const getSubCategoryData = async (categoryIds, pageNo) => {
        try {
            setSubCategoryLoading(true);
            const response = await apiRequest('get', `/category/list?parentId=${categoryIds.toString()}&pageNo=${pageNo}&pageSize=100&type=subCategory`);
            const { data } = response.data;
            if (data?.length > 0) {
                setIsSubCategoryAvailable(true);
                let array = [];
                array = [...subCategoryData, ...data];
                const uniqueArray = [
                    ...new Set(
                        array.map((item) => JSON.stringify(item)),
                    ),
                ].map((item) => JSON.parse(item));
                setSubCategoryData(uniqueArray);
                setSubCategoryLoading(false);
            } else {
                setIsSubCategoryAvailable(false);
                setSubCategoryLoading(false);
            }
        } catch (error) {
            setSubCategoryLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    const getCategoryData = async (pageNo, searchText) => {
        try {
            setCategoryLoading(true);
            const response = await apiRequest('get', `/category/list?pageNo=${pageNo}&pageSize=100&type=category&${searchText ? `searchText=${searchText}` : ''}`);
            const { data } = response.data;
            if (data?.length > 0) {
                setIsCategoryAvailable(true);
                const array = [...categoryData, ...data];
                const uniqueArray = [
                    ...new Set(
                        array.map((item) => JSON.stringify(item)),
                    ),
                ].map((item) => JSON.parse(item));
                setCategoryData(uniqueArray);
                setCategoryLoading(false);
            } else {
                setIsCategoryAvailable(false);
                setCategoryLoading(false);
            }
        } catch (error) {
            setCategoryLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    const loadNextCategory = async (event) => {
        const listBoxNode = event.currentTarget;
        const numberOfPages = categoryPage + 1;
        const position = listBoxNode.scrollTop + listBoxNode.clientHeight;
        if (listBoxNode.scrollHeight - position <= 1 && isCategoryAvailable) {
            setCategoryPage(numberOfPages);
            await getCategoryData(numberOfPages);
        }
    };

    const loadNextSubCategory = async (event) => {
        const listBoxNode = event.currentTarget;
        const numberOfPages = subCategoryPage + 1;
        const position = listBoxNode.scrollTop + listBoxNode.clientHeight;
        if (listBoxNode.scrollHeight - position <= 1 && isSubCategoryAvailable) {
            setSubCategoryPage(numberOfPages);
            await getSubCategoryData(filters?.category, numberOfPages);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (filters?.category?.length > 0) {
                await getSubCategoryData(filters?.category, subCategoryPage);
            }
        }
        fetchData();
    }, [filters?.category]);

    const getManufacturerData = async (pageNo, searchText) => {
        try {
            setManufacturerLoading(true);
            const response = await apiRequest('get', `/product-provider?type=manufacturer&pageNo=${pageNo}&pageSize=100&${searchText ? `searchText=${searchText}` : ''}`);
            const { data } = response.data;
            if (data?.length > 0) {
                setIsManufacturerAvailable(true);
                const array = [...manufacturerData, ...data];
                const uniqueArray = [
                    ...new Set(
                        array.map((item) => JSON.stringify(item)),
                    ),
                ].map((item) => JSON.parse(item));
                setManufacturerData(uniqueArray);
                setManufacturerLoading(false);
            } else {
                setIsManufacturerAvailable(false);
                setManufacturerLoading(false);
            }
        } catch (error) {
            setManufacturerLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    const loadNextManufacturer = async (event) => {
        const listBoxNode = event.currentTarget;
        const numberOfPages = manufacturerPage + 1;
        const position = listBoxNode.scrollTop + listBoxNode.clientHeight;
        if (listBoxNode.scrollHeight - position <= 1 && isManufacturerAvailable) {
            setManufacturerPage(numberOfPages);
            await getManufacturerData(numberOfPages);
        }
    };


    const handleChange = (panel) => (event, isExpanded) => {
        if (panel === 'panel5' && isExpanded) {
            getManufacturerData(manufacturerPage);
            const obj = filters;
            if (!obj?.manufacturer) {
                obj.manufacturer = [];
            }
            setFilters(obj);
        }
        if (panel === 'panel1' && isExpanded) {
            getCategoryData(categoryPage);
            const obj = filters;
            if (!obj?.category) {
                obj.category = [];
            }
            if (!obj?.subCategory) {
                obj.subCategory = [];
            }
            setFilters(obj);
        }
        if (panel === 'panel2' && isExpanded) {
            const obj = filters;
            if (!obj?.minCost) {
                obj.minCost = 1;
                obj.maxCost = 1000;
            }
            setFilters(obj);
        }
        if (panel === 'panel3' && isExpanded) {
            const obj = filters;
            if (!obj?.discontinued) {
                obj.discontinued = null;
            }
            setFilters(obj);
        }
        if (panel === 'panel4' && isExpanded) {
            const obj = filters;
            if (!obj?.features) {
                obj.features = [];
            }
            setFilters(obj);
        }
        if (panel === 'panel6' && isExpanded) {
            const obj = filters;
            if (!obj?.numaricSku) {
                obj.numaricSku = null;
            }
            setFilters(obj);
        }
        setExpanded(isExpanded ? panel : false);
    };

    const handleFilter = async () => {
        if (filters?.category?.length === 0 && filters?.subCategory?.length === 0) {
            showNotification('Please select category or subcategory', 'error');
        } else if (filters?.discontinued === null) {
            showNotification('Please select Disconutinued option', 'error');
        } else if (filters?.numaricSku === null) {
            showNotification('Please select Default Product option', 'error');
        } else if (filters?.features?.length === 0) {
            showNotification('Please select atleast one feature options', 'error');
        } else if (filters?.manufacturer?.length === 0) {
            showNotification('Please select manufacturer', 'error');
        } else if (Object.keys(filters).length === 0) {
            showNotification('Please select any field before apply filter', 'error');
        } else {
            setExpanded(false);
            await applyFilter(filters);
        }
    }

    return (
        <CustomFilterPanelWrapper>
            <Typography variant="body1" style={{ fontWeight: 'bold' }}>Filters</Typography>
            <FilterContent>
                <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                            <Box display="flex" alignItems="center">
                                {(filters?.category?.length > 0 || filters?.subCategory?.length > 0) && (
                                    <CheckIcon color="primary" />
                                )}
                                <Typography component="span">Categories</Typography>
                            </Box>
                            {expanded === 'panel1' && (
                                <Tooltip title="Remove Filter">
                                    <CloseIcon
                                        style={{ color: "gray", cursor: 'pointer', marginRight: 8 }}
                                        onClick={() => {
                                            const obj = filters;
                                            delete obj.category;
                                            delete obj.subCategory;
                                            setFilters(obj);
                                            setExpanded(false);
                                        }}
                                        fontSize="20"
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Autocomplete
                            value={categoryData.filter((c) => filters?.category?.includes(c?.category_id)) || []}
                            id="checkboxes-tags-demo"
                            size="small"
                            fullWidth
                            multiple
                            clearIcon={true}
                            disableCloseOnSelect
                            options={categoryData}
                            getOptionLabel={(option) => option?.name}
                            ListboxProps={{
                                onScroll: (event) => loadNextCategory(event),
                            }}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...optionProps } = props;
                                return (
                                    <li key={key} {...optionProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option.name}
                                    </li>
                                );
                            }}
                            onChange={(event, newValue) => {
                                setFilters((prevData) => ({
                                    ...prevData,
                                    category: newValue?.map((a) => a.category_id)
                                }));
                                setSubCategoryData([]);
                                setSubCategoryPage(1);
                                setCategoryPage(1);
                            }}
                            inputValue={categoryInput === 0 || categoryInput === 'on' ? '' : categoryInput}
                            onInputChange={(e) => {
                                if (e) {
                                    setCategoryInput(e.target.value);
                                    if (e?.target?.value?.length >= 1) {
                                        getCategoryData(1, e?.target?.value);
                                    }
                                    setSubCategoryPage(1);
                                    setCategoryPage(1);
                                }
                            }}
                            renderInput={(params) =>
                                <TextField
                                    fullWidth
                                    {...params}
                                    label="Categories"
                                    name="Categories"
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {categoryLoading ? <CircularProgress color="primary" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />}
                        />
                        <br />
                        <Autocomplete
                            value={subCategoryData?.filter((c) => filters?.subCategory?.includes(c.category_id)) || []}
                            id="checkboxes-tags-demo"
                            size="small"
                            fullWidth
                            multiple
                            clearIcon={true}
                            disableCloseOnSelect
                            options={subCategoryData}
                            getOptionLabel={(option) => `${option?.name} (${option?.parent?.name})`}
                            ListboxProps={{
                                onScroll: (event) => loadNextSubCategory(event),
                            }}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...optionProps } = props;
                                return (
                                    <li key={key} {...optionProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {`${option?.name} (${option?.parent?.name})`}
                                    </li>
                                );
                            }}
                            onChange={(event, newValue) => {
                                setFilters((prevData) => ({
                                    ...prevData,
                                    subCategory: newValue?.map((a) => a.category_id)
                                }));
                            }}
                            renderInput={(params) =>
                                <TextField
                                    fullWidth
                                    {...params}
                                    label="Subcategories"
                                    name="Subcategories"
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {subCategoryLoading ? <CircularProgress color="primary" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />}
                        />
                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel2-content"
                        id="panel2-header"
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                            <Box display="flex" alignItems="center">
                                {filters?.minCost && (
                                    <CheckIcon color="primary" />
                                )}
                                <Typography component="span">Cost</Typography>
                            </Box>
                            {expanded === 'panel2' && (
                                <Tooltip title="Remove Filter">
                                    <CloseIcon
                                        style={{ color: "gray", cursor: 'pointer', marginRight: 8 }}
                                        onClick={() => {
                                            const obj = filters;
                                            delete obj.minCost;
                                            delete obj.maxCost;
                                            setFilters(obj);
                                            setExpanded(false);
                                        }}
                                        fontSize="20"
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Min</InputLabel>
                                    <OutlinedInput
                                        label="Min"
                                        id="standard-adornment-password"
                                        type='number'
                                        value={filters?.minCost || ''}
                                        onChange={(e) => {
                                            if (e.target.value < filters?.maxCost) {
                                                setFilters((prevData) => ({
                                                    ...prevData,
                                                    minCost: e.target.value
                                                }));
                                            } else {
                                                showNotification('The minimum value cannot be greater than or equal to the maximum value.', 'error');
                                            }
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.keyCode === 38 || event.keyCode === 40) {
                                                event.preventDefault();
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <FormControl size="small" fullWidth variant="outlined">
                                    <InputLabel htmlFor="standard-adornment-password">Max</InputLabel>
                                    <OutlinedInput
                                        label="Max"
                                        id="standard-adornment-password"
                                        type='number'
                                        value={filters?.maxCost || ''}
                                        onChange={(e) => {
                                            if (e.target.value < filters?.minCost) {
                                                showNotification('The maximum value cannot be less than or equal to the minimum value.', 'error');
                                            }
                                            setFilters((prevData) => ({
                                                ...prevData,
                                                maxCost: e.target.value
                                            }));
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.keyCode === 38 || event.keyCode === 40) {
                                                event.preventDefault();
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel3-content"
                        id="panel3-header"
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                            <Box display="flex" alignItems="center">
                                {filters?.discontinued && (
                                    <CheckIcon color="primary" />
                                )}
                                <Typography component="span">Discontinued</Typography>
                            </Box>
                            {expanded === 'panel3' && (
                                <Tooltip title="Remove Filter">
                                    <CloseIcon
                                        style={{ color: "gray", cursor: 'pointer', marginRight: 8 }}
                                        onClick={() => {
                                            const obj = filters;
                                            delete obj.discontinued;
                                            setFilters(obj);
                                            setExpanded(false);
                                        }}
                                        fontSize="20"
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box style={{ display: 'flex', gap: 5 }}>
                            <FixedButton
                                variant={filters?.discontinued === 'yes' ? 'contained' : 'outlined'}
                                onClick={() => {
                                    setFilters((prevData) => ({
                                        ...prevData,
                                        discontinued: 'yes'
                                    }));
                                }}
                            >
                                Yes
                            </FixedButton>
                            <FixedButton
                                variant={filters?.discontinued === 'no' ? 'contained' : 'outlined'}
                                onClick={() => {
                                    setFilters((prevData) => ({
                                        ...prevData,
                                        discontinued: 'no'
                                    }));
                                }}
                            >
                                No
                            </FixedButton>
                        </Box>
                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel4-content"
                        id="panel4-header"
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                            <Box display="flex" alignItems="center">
                                {filters?.features?.length > 0 && (
                                    <CheckIcon color="primary" />
                                )}
                                <Typography component="span">Features</Typography>
                            </Box>
                            {expanded === 'panel4' && (
                                <Tooltip title="Remove Filter">
                                    <CloseIcon
                                        style={{ color: "gray", cursor: 'pointer', marginRight: 8 }}
                                        onClick={() => {
                                            const obj = filters;
                                            delete obj.features;
                                            setFilters(obj);
                                            setExpanded(false);
                                        }}
                                        fontSize="20"
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters?.features?.includes('seismic')}
                                        onClick={(e) => {
                                            const arr = [...filters?.features];
                                            if (e.target.checked) {
                                                arr.push('seismic');
                                            } else {
                                                const index = arr.findIndex((a) => a === 'seismic');
                                                if (index > -1) {
                                                    arr.splice(index, 1);
                                                }
                                            }
                                            setFilters((prevData) => ({
                                                ...prevData,
                                                features: arr
                                            }));
                                        }}
                                    />}
                                label="Seismic"
                            />
                        </FormGroup>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters?.features?.includes('da_complient')}
                                        onClick={(e) => {
                                            const arr = [...filters?.features];
                                            if (e.target.checked) {
                                                arr.push('da_complient');
                                            } else {
                                                const index = arr.findIndex((a) => a === 'da_complient');
                                                if (index > -1) {
                                                    arr.splice(index, 1);
                                                }
                                            }
                                            setFilters((prevData) => ({
                                                ...prevData,
                                                features: arr
                                            }));
                                        }
                                        }
                                    />}
                                label="ADA"
                            />
                        </FormGroup>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters?.features?.includes('antimicrobial')}
                                        onClick={(e) => {
                                            const arr = [...filters?.features];
                                            if (e.target.checked) {
                                                arr.push('antimicrobial');
                                            } else {
                                                const index = arr.findIndex((a) => a === 'antimicrobial');
                                                if (index > -1) {
                                                    arr.splice(index, 1);
                                                }
                                            }
                                            setFilters((prevData) => ({
                                                ...prevData,
                                                features: arr
                                            }));
                                        }
                                        }
                                    />}
                                label="Antimicrobial"
                            />
                        </FormGroup>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters?.features?.includes('green')}
                                        onClick={(e) => {
                                            const arr = [...filters?.features];
                                            if (e.target.checked) {
                                                arr.push('green');
                                            } else {
                                                const index = arr.findIndex((a) => a === 'green');
                                                if (index > -1) {
                                                    arr.splice(index, 1);
                                                }
                                            }
                                            setFilters((prevData) => ({
                                                ...prevData,
                                                features: arr
                                            }));
                                        }
                                        }
                                    />}
                                label="Green"
                            />
                        </FormGroup>
                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={expanded === 'panel5'} onChange={handleChange('panel5')}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel5-content"
                        id="panel5-header"
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                            <Box display="flex" alignItems="center">
                                {filters?.manufacturer?.length > 0 && (
                                    <CheckIcon color="primary" />
                                )}
                                <Typography component="span">Manufacturers</Typography>
                            </Box>
                            {expanded === 'panel5' && (
                                <Tooltip title="Remove Filter">
                                    <CloseIcon
                                        style={{ color: "gray", cursor: 'pointer', marginRight: 8 }}
                                        onClick={() => {
                                            const obj = filters;
                                            delete obj.manufacturer;
                                            setFilters(obj);
                                            setExpanded(false);
                                        }}
                                        fontSize="20"
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Autocomplete
                            value={manufacturerData.filter((c) => filters?.manufacturer?.includes(c.product_provider_id)) || []}
                            id="checkboxes-tags-demo"
                            size="small"
                            fullWidth
                            multiple
                            clearIcon={true}
                            disableCloseOnSelect
                            options={manufacturerData}
                            getOptionLabel={(option) => option?.name}
                            ListboxProps={{
                                onScroll: (event) => loadNextManufacturer(event),
                            }}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...optionProps } = props;
                                return (
                                    <li key={key} {...optionProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option.name}
                                    </li>
                                );
                            }}
                            onChange={(event, newValue) => {
                                setFilters((prevData) => ({
                                    ...prevData,
                                    manufacturer: newValue.map((a) => a.product_provider_id),
                                }));
                                setManufacturerPage(1);
                            }}
                            inputValue={inputValue === 0 || inputValue === 'on' ? '' : inputValue}
                            onInputChange={(e) => {
                                if (e) {
                                    setInputValue(e.target.value);
                                    if (e?.target?.value?.length >= 1) {
                                        setManufacturerPage(1);
                                        getManufacturerData(1, e?.target?.value);
                                    }
                                }
                            }}
                            renderInput={(params) =>
                                <TextField
                                    fullWidth
                                    {...params}
                                    label="Manufacturer"
                                    name="Manufacturer"
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {manufacturerLoading ? <CircularProgress color="primary" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />}
                        />
                    </AccordionDetails>
                </Accordion>
                <Accordion expanded={expanded === 'panel6'} onChange={handleChange('panel6')}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel6-content"
                        id="panel6-header"
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                            <Box display="flex" alignItems="center">
                                {filters?.numaricSku && (
                                    <CheckIcon color="primary" />
                                )}
                                <Typography component="span">Unspecified</Typography>
                            </Box>
                            {expanded === 'panel6' && (
                                <Tooltip title="Remove Filter">
                                    <CloseIcon
                                        style={{ color: "gray", cursor: 'pointer', marginRight: 8 }}
                                        onClick={() => {
                                            const obj = filters;
                                            delete obj.numaricSku;
                                            setFilters(obj);
                                            setExpanded(false);
                                        }}
                                        fontSize="20"
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box style={{ display: 'flex', gap: 5 }}>
                            <FixedButton
                                variant={filters?.numaricSku === 'yes' ? 'contained' : 'outlined'}
                                onClick={() => {
                                    setFilters((prevData) => ({
                                        ...prevData,
                                        numaricSku: 'yes'
                                    }));
                                }}
                            >
                                Yes
                            </FixedButton>
                            <FixedButton
                                variant={filters?.numaricSku === 'no' ? 'contained' : 'outlined'}
                                onClick={() => {
                                    setFilters((prevData) => ({
                                        ...prevData,
                                        numaricSku: 'no'
                                    }));
                                }}
                            >
                                No
                            </FixedButton>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </FilterContent>
            <FilterFooter>
                <FixedButton variant="outlined" color="secondary" onClick={() => {
                    applyFilter({});
                    setFilters({});
                    setExpanded(false);
                    localStorage.removeItem('filters');
                }}>Clear</FixedButton>
                <FixedButton variant="outlined" color="primary" onClick={() => handleFilter()}>Apply</FixedButton>
            </FilterFooter>
        </CustomFilterPanelWrapper>
    )
}